import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { setChoicesUser } from "@/lib/choices";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [ready, setReady] = useState(false);
  // True once an explicit sign-in (magic link / login / register) has set the user.
  // Prevents the initial /auth/me check (which may 401 before a token exists) from
  // clobbering a user that was just signed in — a race seen on the magic-link tab.
  const hydratedRef = useRef(false);

  // Scope per-user preferences so a new login never shows the previous user's picks.
  useEffect(() => {
    setChoicesUser(user && user.id ? user.id : (user && user.email) || null);
  }, [user]);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { if (hydratedRef.current) return; setChoicesUser(r.data && (r.data.id || r.data.email)); setUser(r.data); })
      .catch(() => { if (hydratedRef.current) return; setChoicesUser(null); setUser(false); })
      .finally(() => setReady(true));
  }, []);

  const applyAuth = (data) => {
    hydratedRef.current = true;
    if (data.token) localStorage.setItem("rp_token", data.token);
    setChoicesUser(data.id || data.email);
    setUser(data);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    applyAuth(data);
    return data;
  };

  const logout = async () => {
    hydratedRef.current = true;
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("rp_token");
    setUser(false);
  };

  const refreshUser = async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); } catch (e) {}
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refreshUser, applyAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
